import { useState, useEffect, useMemo } from 'react';
import { Box, Flex, Text, Button, Icon, Spinner, useColorMode } from '@chakra-ui/react';
import { LuLayoutGrid } from 'react-icons/lu';
import { useTranslation } from 'react-i18next';
import { fetchCategories } from '../../api/endpointsBackend';
import useIsPhone from '../../hooks/useIsPhone';
import { categoryTint } from '../../utils/categoryTint';

// The chips hang indented past the row's icon; on the phone there's no room
// to spare for that.
function useChipIndent() {
  return useIsPhone() ? 0 : '54px';
}

// On the phone "+N more" is a 44px target, but drawn as a chip the size of
// the others, so the row it sits in doesn't grow a taller pill than the rest.
const PHONE_MORE_CHIP = {
  h: '44px',
  // Half of what the target adds over a chip's ~30px, above and below, so
  // its line is no taller than the others.
  my: '-7px',
  px: 0,
  py: 0,
  borderWidth: 0,
  bg: 'transparent',
  _hover: { bg: 'transparent' },
  sx: {
    '& > span': {
      px: '10px',
      py: '5px',
      // 17.5 + 10 + 2 = a chip's 29.5px.
      lineHeight: '17.5px',
      borderRadius: 'full',
      border: '1px dashed',
      borderColor: 'redesign.border.strong',
    },
  },
};

// How many to show before "+N more". Enough to make the point that the set is
// already there, few enough not to bury the row.
const PREVIEW_COUNT = 6;

// The chip's tint, from the category's own colour. Categories carry a hex from
// the seed; movements render them the same way, so the colour a user learns
// here is the colour they'll keep seeing. The name keeps the hue, moved toward
// the ink only as far as it needs to read on the tint.
function CategoryChip({ category }) {
  const { colorMode } = useColorMode();
  const tint = category.color ? categoryTint(category.color, colorMode) : null;
  return (
    <Flex
      as="span"
      align="center"
      gap="5px"
      pl="8px"
      pr="10px"
      py="5px"
      borderRadius="full"
      bg={tint?.bg || 'redesign.bg.card'}
      fontSize="12px"
      fontWeight={600}
    >
      {category.icon && <Text as="span" fontSize="13px" lineHeight="1">{category.icon}</Text>}
      <Text as="span" color={tint?.fg || 'redesign.text'} noOfLines={1}>
        {category.name}
      </Text>
    </Flex>
  );
}

function GroupLabel({ children }) {
  const indent = useChipIndent();
  return (
    <Text
      textStyle="microLabel"
      mt="14px" mb={0} pl={indent}
    >
      {children}
    </Text>
  );
}

/**
 * The categories row: show the set the user already has, inline.
 *
 * It used to be a button that opened /categories in another tab. Onboarding
 * blocks now, so there is nowhere to send them — and there was never a reason
 * to: the point is "you already have these", which is better made by showing
 * them than by linking to them. Nothing is created here; the row is
 * acknowledged by passing the step.
 *
 * The chips hang under the row's text, indented past the icon, so the set reads
 * as evidence for the sentence above it rather than as a separate control.
 */
export default function OnboardingCategories() {
  const { t } = useTranslation('onboarding');
  const isPhone = useIsPhone();
  const indent = useChipIndent();
  const [categories, setCategories] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    fetchCategories().then((c) => setCategories(c || [])).catch(() => setCategories([]));
  }, []);

  const groups = useMemo(() => {
    const all = categories || [];
    return {
      income: all.filter((c) => c.type === 'Income'),
      expenses: all.filter((c) => c.type === 'Expenses'),
    };
  }, [categories]);

  // A mix, not the first six the API happens to return — in seed order those
  // are every Income category and nothing else, which reads as "you only got
  // income categories". Two income and the rest expenses shows both halves.
  const preview = useMemo(() => {
    const wantIncome = Math.min(2, groups.income.length);
    const picked = [
      ...groups.income.slice(0, wantIncome),
      ...groups.expenses.slice(0, PREVIEW_COUNT - wantIncome),
    ];
    if (picked.length < PREVIEW_COUNT) {
      const rest = [...groups.income, ...groups.expenses].filter((c) => !picked.includes(c));
      picked.push(...rest.slice(0, PREVIEW_COUNT - picked.length));
    }
    return picked;
  }, [groups]);

  const hidden = (categories?.length || 0) - preview.length;

  const moreChip = (
    <Button
      h="auto"
      px="10px"
      py="5px"
      fontSize="12px"
      fontWeight={600}
      borderRadius="full"
      borderWidth="1px"
      borderStyle="dashed"
      borderColor="redesign.border.strong"
      color={isPhone ? 'phone.link' : 'redesign.accent.blue'}
      bg="transparent"
      _hover={{ bg: 'redesign.bg.hover' }}
      onClick={() => setExpanded((e) => !e)}
      {...(isPhone && PHONE_MORE_CHIP)}
    >
      <span>{expanded ? t('wizard.categories.less') : t('wizard.categories.more', { count: hidden })}</span>
    </Button>
  );

  return (
    <Flex
      direction="column"
      align="stretch"
      p="14px"
      mb="10px"
      borderWidth="1px"
      borderColor="redesign.border"
      borderRadius="16px"
      bg="redesign.bg.surface"
    >
      <Flex align="center" gap="14px">
        <Flex
          boxSize="40px"
          borderRadius="12px"
          align="center"
          justify="center"
          flexShrink={0}
          bg="redesign.bg.accent"
          color="redesign.accent.blue"
        >
          <Icon as={LuLayoutGrid} boxSize="18px" strokeWidth={1.7} />
        </Flex>
        <Box flex="1" minW={0}>
          <Text fontSize="14px" fontWeight={600} color="redesign.heading" mb="2px">
            {t('wizard.categories.rowTitle')}
          </Text>
          <Text fontSize="13px" color="redesign.text.muted" lineHeight="1.4">
            {t('wizard.categories.rowDesc', { count: categories?.length ?? 0 })}
          </Text>
        </Box>
      </Flex>

      {categories === null && (
        <Box pl={indent} mt="10px">
          <Spinner size="sm" color="redesign.accent.blue" />
        </Box>
      )}

      {!expanded && !!categories?.length && (
        <Flex flexWrap="wrap" gap="6px" pl={indent} mt="10px" align={isPhone ? 'center' : undefined}>
          {preview.map((c) => (
            <CategoryChip key={c.id} category={c} />
          ))}
          {hidden > 0 && moreChip}
        </Flex>
      )}

      {expanded && ['income', 'expenses'].map((groupKey) => (
        groups[groupKey].length > 0 && (
          <Box key={groupKey}>
            <GroupLabel>{t(`wizard.categories.${groupKey}`)}</GroupLabel>
            <Flex flexWrap="wrap" gap="6px" pl={indent} mt="10px" align={isPhone ? 'center' : undefined}>
              {groups[groupKey].map((c) => <CategoryChip key={c.id} category={c} />)}
              {groupKey === 'expenses' && moreChip}
            </Flex>
          </Box>
        )
      ))}
    </Flex>
  );
}
